import { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { handleError, ErrorSeverity, ErrorContext } from '@/utils/errorHandler';

/**
 * Fetches the user's profile data from the profile table or metadata
 * Retries if the profile row is not found (406 error), up to 5 times with 300ms delay
 */
export async function fetchUserProfile(
  userId: string,
  email: string | null = null,
  user: User | null = null
): Promise<{
  role: string;
  isAdmin: boolean;
  organizationId: string | null;
  subscriptionStatus: string | null;
  hasValidSubscription: boolean;
}> {
  const context: ErrorContext = {
    operation: 'fetchUserProfile',
    additionalInfo: { userId }
  };

  const maxRetries = 20; // Increased from 10
  const delayMs = 1000; // Increased from 500ms to 1s

  async function tryFetchProfile(attempt = 1): Promise<any> {
    try {
      // First get user profile data
      const { data: profileData, error: profileError, status } = await supabase
        .from('user_profiles')
        .select(`
          role, 
          organization_id, 
          is_superuser
        `)
        .eq('id', userId)
        .maybeSingle();

      if (profileData && !profileError) {
        // Get subscription status from organizations table
        let subscriptionStatus = null;
        if (profileData.organization_id) {
          const { data: orgData } = await supabase
            .from('organizations')
            .select('subscription_status')
            .eq('id', profileData.organization_id)
            .maybeSingle();
          
          subscriptionStatus = orgData?.subscription_status || null;
        }

        const validStatuses = ['trial_active', 'active', 'paid_active', 'trialing', 'active-hiveshare'];
        
        return {
          role: profileData.role,
          isAdmin: !!profileData.is_superuser || ['bizadmin', 'owner', 'superadmin'].includes(profileData.role),
          organizationId: profileData.organization_id,
          subscriptionStatus,
          hasValidSubscription: validStatuses.includes(subscriptionStatus || '')
        };
      }

      // If 406 (not found), retry up to maxRetries
      if ((profileError && profileError.code === 'PGRST116') || status === 406) {
        if (attempt < maxRetries) {
          await new Promise(res => setTimeout(res, delayMs));
          return tryFetchProfile(attempt + 1);
        }
      }

      // If no profile or error, fall back to user metadata
      if (user?.user_metadata) {
        const metadata = user.user_metadata;
        const metadataProfile = {
          role: metadata.role || 'user',
          isAdmin: ['bizadmin', 'owner', 'superadmin'].includes(metadata.role || ''),
          organizationId: metadata.organization_id || null,
          subscriptionStatus: null,
          hasValidSubscription: false
        };

        // If we have an organization_id in metadata but couldn't find a profile in the database,
        // try to create the profile now for future use
        if (metadataProfile.organizationId && profileError) {
          try {
            await supabase
              .from('user_profiles')
              .upsert({
                id: userId,
                organization_id: metadataProfile.organizationId,
                role: metadataProfile.role,
                email: email,
                is_superuser: metadataProfile.role === 'superadmin',
                created_at: new Date().toISOString()
              });
          } catch (err) {
            // Ignore upsert errors here
          }
        }
        return metadataProfile;
      }

      // If no user metadata, return defaults
      return {
        role: 'user',
        isAdmin: false,
        organizationId: null,
        subscriptionStatus: null,
        hasValidSubscription: false
      };
    } catch (error) {
      handleError(
        error,
        'Error fetching user profile',
        ErrorSeverity.WARNING,
        context,
        false
      );
      return {
        role: 'user',
        isAdmin: false,
        organizationId: null,
        subscriptionStatus: null,
        hasValidSubscription: false
      };
    }
  }

  return tryFetchProfile();
}
